/**
 * useNativeDeepLinks — Iter 80.
 *
 * When the app is running inside the Capacitor iOS/Android shell, registers
 * a listener for `appUrlOpen` events. Universal links like
 *   https://physiogenic.clinic/refer/ABC123
 * and custom-scheme links like
 *   physiogenic://refer/ABC123
 * are converted to in-app navigations so they land directly on the right
 * React route — no full reload, no logout.
 *
 * On the web (`Capacitor.isNativePlatform()` is false) this hook is a no-op,
 * so it's safe to mount unconditionally inside <App/>.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function useNativeDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    let listener;
    let cancelled = false;

    const wire = async () => {
      try {
        // Lazy import so the web bundle doesn't pull in Capacitor when it
        // doesn't have to. The dynamic imports are tree-shaken away by
        // CRA / webpack for the web target.
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform || !Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        if (cancelled) return;
        listener = await App.addListener('appUrlOpen', ({ url }) => {
          try {
            // Strip the scheme + host so we end up with a React Router path.
            //   physiogenic://refer/ABC123 → /refer/ABC123
            //   https://physiogenic.clinic/refer/ABC123 → /refer/ABC123
            const u = new URL(url);
            const path = (u.pathname || '/') + (u.search || '') + (u.hash || '');
            if (path && path !== '/') navigate(path);
          } catch (_e) {
            // Ignore malformed deep links rather than crash the app.
          }
        });
      } catch (_e) {
        // Capacitor not bundled (we're on the web) — silently skip.
      }
    };
    wire();

    return () => {
      cancelled = true;
      if (listener && listener.remove) listener.remove();
    };
  }, [navigate]);
}
